/**
 * Footer Java - Isolated JavaScript for Dawn Theme
 * Fully scoped to avoid conflicts with Dawn's existing footer
 */

(function() {
  'use strict';

  class FooterJava {
    constructor() {
      this.footerElement = document.querySelector('.footer--java');
      if (!this.footerElement) return;

      this.accordionToggles = this.footerElement.querySelectorAll('[data-footer-java-toggle]');
      this.newsletterForm = this.footerElement.querySelector('[data-footer-java-newsletter]');
      this.newsletterMessage = this.footerElement.querySelector('[data-footer-java-message]');
      
      this.isMobile = window.innerWidth <= 1024;
      this.isSubmitting = false;
      
      this.init();
    }

    init() {
      this.setupAccordions();
      this.setupNewsletter();
      this.setupLanguageCurrencySelector();
      this.setupResizeHandler();
    }

    /**
     * Mobile Accordion Functionality
     */
    setupAccordions() {
      this.accordionToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleAccordion(toggle);
        });

        // Keyboard support
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleAccordion(toggle);
          }
        });
      });
    }

    toggleAccordion(toggle) {
      if (!this.isMobile) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const menuId = toggle.getAttribute('aria-controls');
      const menu = this.footerElement.querySelector(`#${menuId}`);
      
      if (!menu) return;

      // Close other accordions
      this.accordionToggles.forEach(otherToggle => {
        if (otherToggle !== toggle) {
          otherToggle.setAttribute('aria-expanded', 'false');
          const otherMenuId = otherToggle.getAttribute('aria-controls');
          const otherMenu = this.footerElement.querySelector(`#${otherMenuId}`);
          if (otherMenu) {
            otherMenu.classList.remove('is-open');
          }
        }
      });

      // Toggle current accordion
      toggle.setAttribute('aria-expanded', !isExpanded);
      menu.classList.toggle('is-open', !isExpanded);
    }

    /**
     * Language & Currency Selector Handling
     */
    setupLanguageCurrencySelector() {
      const selector = this.footerElement.querySelector('[data-language-currency-selector]');
      const formId = selector ? selector.getAttribute('data-locale-country-form') : null;
      if (!selector || !formId) return;

      selector.addEventListener('change', (e) => {
        const selectedValue = e.target.value; // e.g., "US|en" or "FR|fr"
        const [countryCode, localeCode] = selectedValue.split('|');

        const form = document.getElementById(formId);
        if (!form) return;

        const countryInput = form.querySelector('input[name="country_code"]');
        const localeInput = form.querySelector('input[name="locale_code"]');
        if (countryInput && localeInput) {
          countryInput.value = countryCode;
          localeInput.value = localeCode;
          form.submit();
        }
      });
    }

    handleLanguageCurrencyChange(value) {
      // Deprecated: replaced by direct localization form submit in setupLanguageCurrencySelector
    }

    simpleRedirect(language, currency) {
      const currentUrl = new URL(window.location);
      const pathParts = currentUrl.pathname.split('/');
      
      // Remove existing language code if present
      if (pathParts[1] === 'en' || pathParts[1] === 'fr') {
        pathParts.splice(1, 1);
      }
      
      // Add new language code (except for default English)
      if (language === 'fr') {
        pathParts.splice(1, 0, 'fr');
      }
      
      // Add currency parameter
      const searchParams = new URLSearchParams(currentUrl.search);
      searchParams.set('currency', currency);
      
      // Reconstruct URL
      const newPath = pathParts.join('/') || '/';
      window.location.href = currentUrl.origin + newPath + '?' + searchParams.toString();
    }

    /**
     * Newsletter Form Handling (DEBUG VERSION)
     */
    setupNewsletter() {
      if (!this.newsletterForm) return;

      this.newsletterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleNewsletterSubmit(e.target);
      });
    }

    async handleNewsletterSubmit(form) {
      if (this.isSubmitting) return;

      const formData = new FormData(form);
      const email = formData.get('contact[email]');

      // Basic email validation
      if (!this.isValidEmail(email)) {
        this.showNewsletterMessage('Please enter a valid email address.', 'error');
        return;
      }

      this.isSubmitting = true;

      const submitButton = form.querySelector('.footer--java__newsletter-submit');
      const originalButtonContent = submitButton ? submitButton.textContent : '';

      // Show loading state
      if (submitButton) {
        submitButton.textContent = '...';
        submitButton.disabled = true;
      }

      try {
        const actionUrl = form.action || '/contact#footer-newsletter';
        console.log('[FooterJava] Submitting newsletter to:', actionUrl);

        const response = await fetch(actionUrl, {
          method: 'POST',
          body: formData
          // no headers – let the browser set Content-Type for FormData
        });

        console.log('[FooterJava] Newsletter response status:', response.status, 'OK?', response.ok);
        console.log('[FooterJava] Newsletter final URL:', response.url);

        // Read response body (HTML) and preview first 200 chars
        const text = await response.text();
        console.log('[FooterJava] Newsletter response preview:', text.slice(0, 200));

        // For now, treat any non-throwing response as success,
        // since we know the contact reaches Klaviyo.
        this.showNewsletterMessage('Thank you for subscribing!', 'success');
        form.reset();

      } catch (error) {
        console.error('[FooterJava] Newsletter subscription error:', error);
        this.showNewsletterMessage('Something went wrong. Please try again.', 'error');
      } finally {
        if (submitButton) {
          submitButton.textContent = originalButtonContent;
          submitButton.disabled = false;
        }
        this.isSubmitting = false;
      }
    }

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    showNewsletterMessage(message, type) {
      if (!this.newsletterMessage) return;

      this.newsletterMessage.textContent = message;
      this.newsletterMessage.className = `footer--java__newsletter-message ${type}`;

      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.newsletterMessage.className = 'footer--java__newsletter-message';
      }, 5000);

      // Smooth scroll to message if it's off-screen
      this.scrollToMessage();
    }

    scrollToMessage() {
      if (!this.newsletterMessage) return;

      const rect = this.newsletterMessage.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isVisible) {
        this.newsletterMessage.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }

    /**
     * Responsive Handler
     */
    setupResizeHandler() {
      let resizeTimeout;
      
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.handleResize();
        }, 250);
      });
    }

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1024;
      
      // If switching from mobile to desktop, close all accordions
      if (wasMobile && !this.isMobile) {
        this.accordionToggles.forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
          const menuId = toggle.getAttribute('aria-controls');
          const menu = this.footerElement.querySelector(`#${menuId}`);
          if (menu) {
            menu.classList.remove('is-open');
          }
        });
      }
    }
  }

  /**
   * Initialize Footer Java when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new FooterJava();
    });
  } else {
    new FooterJava();
  }

  // Export for potential external use
  if (typeof window !== 'undefined') {
    window.FooterJava = FooterJava;
  }

})();
